"use client";

import { useMemo, useState } from "react";
import { ArrowRight, Button, Check, Checkbox, CircleAlert, Icon, Textarea, cx } from "@/components";
import { summarizePaste, delimiterLabel } from "./pasteSummary";

/**
 * First-class paste (FAIL-473) — the create-surface replacement for the bare
 * homepage paste widget. As you paste, it runs the same client-safe
 * `parseDelimited` the import uses and shows live confidence: delimiter, row ×
 * column counts, a preview grid, and the free-tier ceiling — so you SEE it
 * parsed before committing. "First row is headers" flips how the grid is read;
 * when off we synthesize a header row so the downstream `analyzeGrid` (which
 * always expects one) still gets column names. Submitting hands the text to the
 * existing paste pipeline unchanged.
 */
// A single instructional line — NOT multi-row sample data, which in a monospace
// field reads like content is already pasted (FAIL-473 follow-up).
const PASTE_EXAMPLE = "Paste your rows here — e.g. Name, Address, City, State (keep the header row)";
const PREVIEW_ROWS = 10;

export function PastePanel({ loading, onSubmit }: { loading: boolean; onSubmit: (text: string) => void }) {
  const [text, setText] = useState("");
  const [hasHeader, setHasHeader] = useState(true);
  const [editing, setEditing] = useState(false);
  const summary = useMemo(() => summarizePaste(text, { hasHeader, previewRows: PREVIEW_ROWS }), [text, hasHeader]);

  const structured = !summary.empty && summary.columnCount > 1 && summary.dataRowCount >= 1;
  const needsDelimitedData = !summary.empty && !structured;
  const rejected = summary.ceiling.level === "reject";
  const canSubmit = structured && !rejected && !loading;

  function submit() {
    if (!canSubmit) return;
    // The import path always expects a header row; if the user says there isn't
    // one, prepend the synthesized Column N names so nothing is misread as data.
    const payload = hasHeader ? text : `${summary.header.join("\t")}\n${text}`;
    onSubmit(payload);
  }

  function leaveEditor() {
    setText("");
    setHasHeader(true);
    setEditing(false);
  }

  const hiddenRows = summary.dataRowCount - summary.preview.length;

  return (
    <div className={cx("pz-paste", editing && "pz-paste--editing", !summary.empty && "pz-paste--has-data")}>
      <Textarea
        id="intake-paste"
        className="pz-paste__input"
        rows={6}
        value={text}
        placeholder={PASTE_EXAMPLE}
        aria-label="Paste spreadsheet rows"
        spellCheck={false}
        onChange={(e) => {
          const next = e.target.value;
          setText(next);
          const nextSummary = summarizePaste(next, { hasHeader, previewRows: PREVIEW_ROWS });
          if (nextSummary.columnCount > 1 && nextSummary.dataRowCount >= 1) setEditing(true);
        }}
      />

      {needsDelimitedData ? (
        <p className="pz-paste__guidance" role="status">
          Copy rows with column headings from Excel or Google Sheets, then paste them here.
        </p>
      ) : null}

      {structured ? (
        <>
          <div className="pz-paste__status">
            <span className="pz-paste__count">
              <Icon icon={Check} size="sm" />
              {summary.dataRowCount.toLocaleString()} {summary.dataRowCount === 1 ? "row" : "rows"} ×{" "}
              {summary.columnCount} {summary.columnCount === 1 ? "column" : "columns"}
            </span>
            <span className="pz-paste__delim">{delimiterLabel(summary.delimiter)}</span>
            <Checkbox
              className="pz-paste__toggle"
              checked={hasHeader}
              onChange={(e) => setHasHeader(e.target.checked)}
              label="First row contains column headings"
            />
          </div>
          <div className="pz-paste__preview-wrap">
            <table className="pz-paste__preview">
              <thead>
                <tr>
                  {summary.header.map((h, i) => (
                    <th key={i} scope="col">{h.trim() === "" ? "—" : h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {summary.preview.map((row, r) => (
                  <tr key={r}>
                    {row.map((cell, c) => (
                      <td key={c}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {hiddenRows > 0 ? (
            <p className="pz-paste__more">+{hiddenRows.toLocaleString()} more {hiddenRows === 1 ? "row" : "rows"}</p>
          ) : null}

          {summary.ceiling.message ? (
            <p className={cx("pz-paste__ceiling", rejected && "pz-paste__ceiling--reject")}>
              <Icon icon={CircleAlert} size="sm" />
              {summary.ceiling.message}
            </p>
          ) : null}
        </>
      ) : null}

      <div className="pz-paste__actions">
        {editing ? (
          <Button variant="secondary" onClick={leaveEditor}>
            Back
          </Button>
        ) : null}
        <Button
          variant="primary"
          loading={loading}
          disabled={!canSubmit}
          iconTrailing={<Icon icon={ArrowRight} size="sm" />}
          onClick={submit}
        >
          Generate map
        </Button>
      </div>
    </div>
  );
}
